import { GRADIENT_HEADER } from '@/colors/gradient.colors';
import { Button } from '@/components/ui/button';
import ListItem from '@/components/ui/ListItem';
import {
	NavigationMenu,
	NavigationMenuContent,
	NavigationMenuItem,
	NavigationMenuLink,
	NavigationMenuList,
	NavigationMenuTrigger,
	navigationMenuTriggerStyle,
} from '@/components/ui/navigation-menu';
import { cn } from '@/lib/utils';
import { getImageUrl } from '@/utils/cloudinary.utils';
import { useMemoizedFn } from 'ahooks';
import { AnimatePresence, motion } from 'framer-motion';
import {
	ChevronDown,
	ChevronUp,
	Home,
	Leaf,
	Menu,
	ShoppingCart,
	ThumbsUp,
	X,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CLIENT_ROUTES } from '../../../../shared/constants/routes.constants';
import { generatePath } from '../../../../shared/services/app.shared-service';
import type { SettingsStringKeys } from '../../../../shared/types/settings.entity';
import {
	linksMoreItems,
	linksSocialItems,
} from '../../constants/links.constants';
import { useCart } from '../../hooks/useCart';
import { useLocale } from '../../hooks/useLocale';
import { useCategoriesList } from '../../services/category.api';
import { useSettings } from '../../services/settings.api';
import type { GetFirstParameter } from '../../types/types';
import LinkSkeleton from '../skeletons/LinkSkeleton';
import ListItemSkeleton from '../skeletons/ListItemSkeleton';
import { Badge } from '../ui/badge';
import HeaderImageTitle from './HeaderImageTitle';

export default function Header() {
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
	const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);
	const [isMoreOpen, setIsMoreOpen] = useState(false);
	const [isSocialOpen, setIsSocialOpen] = useState(false);
	const { data: settings, isLoading: isLoadingSettings } = useSettings();
	const linksSocialItemsDisplay = useMemo(
		() => linksSocialItems.filter((link) => settings?.[link.key]),
		[settings]
	);
	const { data: categories, isLoading: isLoadingCategories } =
		useCategoriesList();
	const categoriesDisplay = useMemo(
		() => (categories ?? []).filter((cat) => cat.menuDisplay),
		[categories]
	);

	const { cart } = useCart();
	const { t } = useLocale('client.header');
	const { t: tGeneric } = useLocale('generic');

	const cartItemsCount = useMemo(
		() =>
			cart.items.reduce((total, item) => total + (item.quantity || 1), 0),
		[cart.items]
	);

	const toggleMobileMenu = useMemoizedFn(() => {
		setIsMobileMenuOpen(!isMobileMenuOpen);
		if (isMobileMenuOpen) {
			setIsCategoriesOpen(false);
			setIsMoreOpen(false);
		}
	});

	// Prevent body scroll when mobile menu is open
	useEffect(() => {
		document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
		return () => {
			document.body.style.overflow = '';
		};
	}, [isMobileMenuOpen]);

	return (
		<header className="sticky top-0 z-50 bg-secondary-background border-b border-background-border">
			<div className="sm:px-6 lg:px-8 px-4 mx-auto">
				<div className="flex items-center justify-between h-16">
					<HeaderImageTitle />

					{/* Desktop Nav */}
					<NavigationMenu className="md:flex hidden">
						<NavigationMenuList>
							<NavigationMenuItem>
								<Link
									className={cn(
										navigationMenuTriggerStyle(),
										'flex items-center space-x-2'
									)}
									to={generatePath({
										route: [CLIENT_ROUTES.HOME],
									})}>
									<div className="flex items-center space-x-1">
										<Home className="w-4 h-4" />
										<span>{t('home')}</span>
									</div>
								</Link>
							</NavigationMenuItem>
							<NavigationMenuItem>
								<Link
									className={cn(
										navigationMenuTriggerStyle(),
										'flex items-center space-x-2'
									)}
									to={generatePath({
										route: [CLIENT_ROUTES.PRODUCTS.BASE],
									})}>
									<div className="flex items-center space-x-1">
										<Leaf className="w-4 h-4" />
										<span>{t('allProducts')}</span>
									</div>
								</Link>
							</NavigationMenuItem>
							<NavigationMenuItem>
								<Link
									className={cn(
										navigationMenuTriggerStyle(),
										'flex items-center space-x-2'
									)}
									to={generatePath({
										route: [
											CLIENT_ROUTES.PRODUCTS.BASE,
											CLIENT_ROUTES.PRODUCTS.RECOMMENDED,
										],
									})}>
									<div className="flex items-center space-x-1">
										<ThumbsUp className="w-4 h-4" />
										<span>{t('recommended')}</span>
									</div>
								</Link>
							</NavigationMenuItem>
							<NavigationMenuItem>
								<NavigationMenuTrigger>
									{t('categories')}
								</NavigationMenuTrigger>
								<NavigationMenuContent>
									<ul className="grid w-[600px] gap-3 p-4 md:grid-cols-2">
										{!isLoadingCategories ? (
											<>
												{categoriesDisplay.map(
													(cat) => (
														<ListItem
															className="h-full"
															key={cat.id}
															imageUrl={getImageUrl(
																cat.imageId
															)}
															href={generatePath({
																route: [
																	CLIENT_ROUTES
																		.PRODUCTS
																		.BASE,
																	CLIENT_ROUTES
																		.PRODUCTS
																		.CATEGORY_PRODUCTS,
																],
																params: {
																	categoryId:
																		cat.id,
																},
															})}
															title={cat.name}>
															{cat.description}
														</ListItem>
													)
												)}
											</>
										) : (
											new Array(5)
												.fill(0)
												.map((_, i) => (
													<ListItemSkeleton key={i} />
												))
										)}
									</ul>
								</NavigationMenuContent>
							</NavigationMenuItem>
							<NavigationMenuItem>
								<NavigationMenuTrigger>
									{t('more')}
								</NavigationMenuTrigger>
								<NavigationMenuContent>
									<ul className="grid gap-3 p-6 md:w-[400px] lg:w-[500px] lg:grid-cols-[.75fr_1fr]">
										<li className="row-span-3">
											<NavigationMenuLink asChild>
												<a
													className={`bg-gradient-to-b ${GRADIENT_HEADER} flex flex-col justify-end w-full h-full p-6 no-underline transition-colors rounded-md outline-none select-none`}
													href={generatePath({
														route: [
															CLIENT_ROUTES.ARCHITECTS_DESIGNERS,
														],
													})}>
													<div className="mt-4 mb-2 text-lg font-medium text-primary-foreground">
														{tGeneric(
															'architectsDesigners'
														)}
													</div>
													<p className="text-primary-foreground/90 text-sm leading-tight">
														{tGeneric(
															'architectsDesignersDescription'
														)}
													</p>
												</a>
											</NavigationMenuLink>
										</li>
										{linksMoreItems.map((item) => (
											<ListItem
												key={item.href}
												href={item.href}
												title={t(
													item.title as GetFirstParameter<
														typeof t
													>
												)}>
												{t(
													item.description as GetFirstParameter<
														typeof t
													>
												)}
											</ListItem>
										))}
									</ul>
								</NavigationMenuContent>
							</NavigationMenuItem>
							<NavigationMenuItem>
								<NavigationMenuTrigger>
									{t('socialLinks')}
								</NavigationMenuTrigger>
								<NavigationMenuContent>
									<ul className="grid w-[600px] gap-3 p-4 md:grid-cols-2">
										{!isLoadingSettings ? (
											<>
												{linksSocialItemsDisplay.map(
													(link) => (
														<ListItem
															key={link.key}
															Icon={link.icon}
															href={
																settings?.[
																	link.key as SettingsStringKeys
																] || '#'
															}
															target="_blank"
															rel="noopener noreferrer"
															title={t(
																link.title as GetFirstParameter<
																	typeof t
																>
															)}></ListItem>
													)
												)}
											</>
										) : (
											new Array(5)
												.fill(0)
												.map((_, i) => (
													<ListItemSkeleton key={i} />
												))
										)}
									</ul>
								</NavigationMenuContent>
							</NavigationMenuItem>
						</NavigationMenuList>
					</NavigationMenu>

					{/* Cart + Mobile Menu Btn */}
					<div className="flex items-center space-x-4">
						<Link
							to={generatePath({
								route: [CLIENT_ROUTES.CART],
							})}
							aria-describedby="Go to cart page"
							aria-description="Go to cart page"
							discernible-name="Go to cart page"
							className="hover:text-primary relative p-2 text-secondary transition-colors">
							<ShoppingCart className="w-6 h-6" />
							{cartItemsCount > 0 && (
								<Badge className="-top-1 -right-1 absolute flex items-center justify-center w-5 h-5 text-xs text-primary-foreground bg-primary rounded-full">
									{cartItemsCount}
								</Badge>
							)}
						</Link>
						<Button
							variant="ghost"
							size="icon"
							className="md:hidden"
							onClick={toggleMobileMenu}
							aria-label={
								isMobileMenuOpen ? 'Close menu' : 'Open menu'
							}>
							{isMobileMenuOpen ? (
								<X className="w-6 h-6" />
							) : (
								<Menu className="w-6 h-6" />
							)}
						</Button>
					</div>
				</div>
			</div>

			<AnimatePresence>
				{isMobileMenuOpen && (
					<motion.div
						initial={{ opacity: 0, height: 0 }}
						animate={{ opacity: 1, height: 'auto' }}
						exit={{ opacity: 0, height: 0 }}
						transition={{ duration: 0.3 }}
						className="md:hidden fixed inset-0 z-50 px-4 pb-6 overflow-y-auto bg-secondary-background">
						<div className="flex items-center justify-between h-16 mb-4">
							<HeaderImageTitle />
							<Button
								variant="ghost"
								size="icon"
								onClick={toggleMobileMenu}
								aria-label="Close menu">
								<X className="w-6 h-6" />
							</Button>
						</div>

						{/* Home */}
						<Link
							to={generatePath({
								route: [CLIENT_ROUTES.HOME],
							})}
							onClick={() => setIsMobileMenuOpen(false)}
							className="hover:bg-background flex items-center gap-2 px-3 py-2 text-base font-medium rounded-md">
							<Home className="w-5 h-5" />
							<span>{t('home')}</span>
						</Link>

						<Link
							to={generatePath({
								route: [CLIENT_ROUTES.PRODUCTS.BASE],
							})}
							onClick={() => setIsMobileMenuOpen(false)}
							className="hover:bg-background flex items-center gap-2 px-3 py-2 text-base font-medium rounded-md">
							<Leaf className="w-5 h-5" />
							<span>{t('allProducts')}</span>
						</Link>

						{/* Favorites */}
						<Link
							to={generatePath({
								route: [
									CLIENT_ROUTES.PRODUCTS.BASE,
									CLIENT_ROUTES.PRODUCTS.RECOMMENDED,
								],
							})}
							onClick={() => setIsMobileMenuOpen(false)}
							className="hover:bg-background flex items-center gap-2 px-3 py-2 text-base font-medium rounded-md">
							<ThumbsUp className="w-5 h-5" />
							<span>{t('recommended')}</span>
						</Link>

						{/* Categories */}
						<div>
							<button
								onClick={() =>
									setIsCategoriesOpen(!isCategoriesOpen)
								}
								className="hover:bg-background flex items-center justify-between w-full px-3 py-2 text-base font-medium text-muted rounded-md">
								<span>{t('categories')}</span>
								{isCategoriesOpen ? (
									<ChevronUp className="w-5 h-5" />
								) : (
									<ChevronDown className="w-5 h-5" />
								)}
							</button>
							<AnimatePresence>
								{isCategoriesOpen && (
									<motion.div
										initial={{ opacity: 0, height: 0 }}
										animate={{ opacity: 1, height: 'auto' }}
										exit={{ opacity: 0, height: 0 }}
										transition={{ duration: 0.2 }}
										className="mt-2 ml-4 space-y-1 overflow-hidden">
										{!isLoadingCategories ? (
											<>
												{categoriesDisplay.map(
													(cat) => (
														<Link
															key={cat.id}
															to={generatePath({
																route: [
																	CLIENT_ROUTES
																		.PRODUCTS
																		.BASE,
																	CLIENT_ROUTES
																		.PRODUCTS
																		.CATEGORY_PRODUCTS,
																],
																params: {
																	categoryId:
																		cat.id,
																},
															})}
															onClick={() =>
																setIsMobileMenuOpen(
																	false
																)
															}
															className="hover:bg-background block px-3 py-2 text-sm rounded-md">
															{cat.name}
														</Link>
													)
												)}
											</>
										) : (
											new Array(3)
												.fill(null)
												.map((_, index) => (
													<LinkSkeleton key={index} />
												))
										)}
									</motion.div>
								)}
							</AnimatePresence>
						</div>

						{/* More */}
						<div>
							<button
								onClick={() => setIsMoreOpen(!isMoreOpen)}
								className="hover:bg-background flex items-center justify-between w-full px-3 py-2 text-base font-medium text-muted rounded-md">
								<span>{t('more')}</span>
								{isMoreOpen ? (
									<ChevronUp className="w-5 h-5" />
								) : (
									<ChevronDown className="w-5 h-5" />
								)}
							</button>
							<AnimatePresence>
								{isMoreOpen && (
									<motion.div
										initial={{ opacity: 0, height: 0 }}
										animate={{ opacity: 1, height: 'auto' }}
										exit={{ opacity: 0, height: 0 }}
										transition={{ duration: 0.2 }}
										className="mt-2 ml-4 space-y-1 overflow-hidden">
										<Link
											to={generatePath({
												route: [
													CLIENT_ROUTES.ARCHITECTS_DESIGNERS,
												],
											})}
											onClick={() =>
												setIsMobileMenuOpen(false)
											}
											className="hover:bg-background block px-3 py-2 text-sm font-medium text-primary rounded-md">
											{tGeneric('architectsDesigners')}
										</Link>
										{linksMoreItems.map((item) => (
											<Link
												key={item.href}
												to={item.href}
												onClick={() =>
													setIsMobileMenuOpen(false)
												}
												className="hover:bg-background block px-3 py-2 text-sm text-secondary rounded-md">
												{t(
													item.title as GetFirstParameter<
														typeof t
													>
												)}
											</Link>
										))}
									</motion.div>
								)}
							</AnimatePresence>
						</div>

						{/* Social Links */}
						<div>
							<button
								onClick={() => setIsSocialOpen(!isSocialOpen)}
								className="hover:bg-background flex items-center justify-between w-full px-3 py-2 text-base font-medium text-muted rounded-md">
								<span>{t('socialLinks')}</span>
								{isSocialOpen ? (
									<ChevronUp className="w-5 h-5" />
								) : (
									<ChevronDown className="w-5 h-5" />
								)}
							</button>
							<AnimatePresence>
								{isSocialOpen && (
									<motion.div
										initial={{ opacity: 0, height: 0 }}
										animate={{ opacity: 1, height: 'auto' }}
										exit={{ opacity: 0, height: 0 }}
										transition={{ duration: 0.2 }}
										className="mt-2 ml-4 space-y-1 overflow-hidden">
										{!isLoadingSettings ? (
											<>
												{linksSocialItemsDisplay.map(
													(link) => (
														<Link
															key={link.key}
															to={
																settings?.[
																	link.key as SettingsStringKeys
																] || '#'
															}
															target="_blank"
															rel="noopener noreferrer"
															onClick={() =>
																setIsMobileMenuOpen(
																	false
																)
															}
															className="hover:bg-background block px-3 py-2 text-sm rounded-md">
															{t(
																link.title as GetFirstParameter<
																	typeof t
																>
															)}
														</Link>
													)
												)}
											</>
										) : (
											new Array(3)
												.fill(null)
												.map((_, index) => (
													<LinkSkeleton key={index} />
												))
										)}
									</motion.div>
								)}
							</AnimatePresence>
						</div>
					</motion.div>
				)}
			</AnimatePresence>
		</header>
	);
}
